import { useEffect } from 'react';
import {
  getFileTransferState,
  getFileTransferUploadingFilesCount,
  getRequestMemberIds,
} from '../../../features/fileTransfer/fileTransfer.selector';
import {
  FileTransferEventName,
  FileTransferStateType,
} from '../../../features/fileTransfer/fileTransfer.type';
import {
  addNotification,
  dismissNotification,
} from '../../../features/notifications/notifications.slice';
import { useCallParticipants } from '../../../hooks/useCallParticipants';
import { useAppDispatch, useAppSelector } from '../../../hooks/useReduxToolkit';
import { getNotifications } from '../../../features/notifications/notifications.selector';
import { NotificationAddData, toast } from '@doxyme/ui/components/foundations';
import { RealtimeRole } from '../../../features/realtime/realtime.types';
import { getApolloSessionDetails } from '../../../features/apolloSession/apolloSession.selectors';
import { store } from '../../../stores/redux';
import { resetFileTransferState } from '../../../features/fileTransfer/fileTransfer.slice';
import { sendFileTransferRealtimeMessage } from './sendFileTransferRealtimeMessage';
import { useIntl } from '@doxyme/locale';
import { useProviderViewWaitingPatients } from '../../../hooks/useProviderViewWaitingPatients';

export enum NotificationId {
  WaitingForResponse = 'fileTransferWaitForResponse',
  Uploading = 'fileTransferUploading',
  WaitForDownload = 'fileTransferWaitForDownload',
  UserIsDownloading = 'fileTransferUserIsDownloading',
}

export const dismissAllNotifications = async (): Promise<void> => {
  await Promise.all(
    Object.values(NotificationId).map(async (id) =>
      store.dispatch(dismissNotification({ id })),
    ),
  );
};

export const useFileTransferNotifications = (): void => {
  const { callParticipantsMap } = useCallParticipants();
  const { waitingPatientsMap } = useProviderViewWaitingPatients();
  const dispatch = useAppDispatch();
  const fileTransferState = useAppSelector(getFileTransferState);
  const uploadingFilesCount = useAppSelector(
    getFileTransferUploadingFilesCount,
  );
  const requestMemberIds = useAppSelector(getRequestMemberIds);
  const firstMemberId = requestMemberIds[0] as string;
  const name = (
    callParticipantsMap[firstMemberId] ?? waitingPatientsMap[firstMemberId]
  )?.state?.username as string;
  const { formatMessage } = useIntl();
  const { role } = useAppSelector(getApolloSessionDetails);
  const notificationsInQueue = useAppSelector(getNotifications);

  const showNotification = (notiData: NotificationAddData): boolean => {
    if (notificationsInQueue.some(({ id: qid }) => qid === notiData.id)) {
      return false;
    }

    dispatch(addNotification(notiData));
    return true;
  };

  const onCancelUpload = (): void => {
    dispatch(resetFileTransferState());
    void sendFileTransferRealtimeMessage(
      requestMemberIds,
      FileTransferEventName.UploadCancelled,
    );
  };

  const onCancelPatientDownload = (): void => {
    dispatch(resetFileTransferState());
    void sendFileTransferRealtimeMessage(
      requestMemberIds,
      FileTransferEventName.DownloadStoppedRemotely,
    );
  };

  const numberOfPatients = requestMemberIds.length;
  useEffect(() => {
    switch (fileTransferState) {
      case FileTransferStateType.Uploading: {
        showNotification({
          id: NotificationId.Uploading,
          type: 'fileTransferUploading',
          callback: onCancelUpload,
          title: formatMessage(
            {
              id: 'fileTransfer.notification.uploadingFiles',
            },
            {
              uploadingFilesCount,
            },
          ),
        });

        break;
      }
      case FileTransferStateType.RequestSent: {
        showNotification({
          id: NotificationId.WaitingForResponse,
          type: 'fileTransferWaitForResponse',
          titlePlaceholderValues: { name },
        });
        break;
      }
      case FileTransferStateType.UploadComplete: {
        void dispatch(dismissNotification({ id: NotificationId.Uploading }));

        toast.success({
          title: formatMessage(
            { id: 'fileTransfer.notification.uploadedSuccessfully' },
            { uploadingFilesCount },
          ),
        });

        showNotification({
          id: NotificationId.WaitForDownload,
          type: 'fileTransferWaitForDownload',
          titlePlaceholderValues: {
            name,
            numberOfPatients,
          },
          callback: onCancelPatientDownload,
        });
        break;
      }
      case FileTransferStateType.FilesReadyForDownload: {
        void dispatch(
          dismissNotification({ id: NotificationId.WaitingForResponse }),
        );
        break;
      }
      case FileTransferStateType.RemoteDownloading: {
        void dispatch(
          dismissNotification({ id: NotificationId.WaitForDownload }),
        );
        showNotification({
          id: NotificationId.UserIsDownloading,
          type: 'fileTransferUserIsDownloading',
          titlePlaceholderValues: {
            name,
            numberOfPatients,
          },
          callback: onCancelPatientDownload,
        });
        break;
      }
      case FileTransferStateType.RemoteDownloadCompleted: {
        void dispatch(
          dismissNotification({ id: NotificationId.UserIsDownloading }),
        );

        toast.success({
          title: formatMessage(
            { id: 'fileTransfer.notification.remoteDownloadedSuccessfully' },
            { uploadingFilesCount, numberOfPatients },
          ),
        });

        break;
      }
      case FileTransferStateType.DownloadCompleted: {
        toast.success({
          title: formatMessage({
            id: 'fileTransfer.notification.localDownloadedSuccessfully',
          }),
        });

        break;
      }
      case FileTransferStateType.RequestRejected: {
        void dispatch(
          dismissNotification({ id: NotificationId.WaitingForResponse }),
        );
        break;
      }
      case FileTransferStateType.DownloadCancelled: {
        void dispatch(
          dismissNotification({ id: NotificationId.WaitForDownload }),
        );
        void dispatch(
          dismissNotification({ id: NotificationId.UserIsDownloading }),
        );
        if (role === RealtimeRole.Patient) {
          toast.warning({
            title: formatMessage(
              { id: 'fileTransfer.notification.providerCancelledRequest' },
              { name },
            ),
          });
        }
        break;
      }
      case FileTransferStateType.RemoteDownloadCancelled: {
        toast.warning({
          title: formatMessage(
            { id: 'fileTransfer.notification.cancelledRemoteDownload' },
            { name },
          ),
        });

        break;
      }
      case FileTransferStateType.ServerError: {
        void dismissAllNotifications();
        break;
      }

      default:
    }
  }, [fileTransferState, name, requestMemberIds, uploadingFilesCount]);
};
